import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { PermissionContext } from "../../contexts/PermissionContext";
import PrimaryButton from "../Buttons/PrimaryButton";
import { NoticeIcon, ReviewIcon, WriteIcon } from "../Icons";
import CheckedIcon from "../Icons/CheckedIcon";
import NoticeInformationListModal from "./NoticeInformationListModal";
import { ConfirmActionModal } from "./index";
import { NOTICE_STATUS } from "../../utils/common";
import { createWorkflowTestId, workflowStepSlug } from "@utils/workflowTestId";

const WorkflowProgressNotice = ({
  projectManagerId,
  noticeStatus,
  noticeInfoWriteStatusCd,
  noticeInfo,
  noticeInfoComponents = [],
  scanComponents = [],
  updateNoticeInfoNoticeInfoWriteStatus,
  onRequestReview,
  onRejectReview,
  onConfirmNotice,
  onReDistribution,
  dataTestId = "workflow-progress-notice",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const tid = createWorkflowTestId(dataTestId);
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const canManageWorkflow = canManageProjectWorkflow(projectManagerId);

  // Modal states
  const [showNoticeInfoModal, setShowNoticeInfoModal] = useState(false);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "",
    confirmButtonVariant: "primary",
    onConfirm: null,
  });

  // Button states
  const [noticeInfoCompleted, setNoticeInfoCompleted] = useState(false);

  // Calculate write step label
  const getWriteStepLabel = () => {
    if (noticeStatus === NOTICE_STATUS.REQUEST || noticeStatus === NOTICE_STATUS.ON_WRITE) {
      if (noticeInfoWriteStatusCd === NOTICE_STATUS.REQUEST) {
        return `${t("project.notice-infor.write")} 0/1`;
      } else if (noticeInfoWriteStatusCd === NOTICE_STATUS.DONE) {
        return `${t("project.notice-infor.write")} 1/1`;
      }
    }
    return t("project.notice-infor.write");
  };

  useEffect(() => {
    setNoticeInfoCompleted(noticeInfoWriteStatusCd === NOTICE_STATUS.DONE || noticeInfoWriteStatusCd === NOTICE_STATUS.UNNECESSARY);
  }, [noticeInfoWriteStatusCd]);

  // Initialize table data when modal opens
  const handleOpenNoticeInfoModal = () => {
    setShowNoticeInfoModal(true);
  };


  const formatModalTitle = (title) => (typeof title === "string" ? title.toUpperCase() : title);

  const showConfirmation = (title, description, confirmText, confirmButtonVariant, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title: formatModalTitle(title),
      description,
      confirmText,
      confirmButtonVariant,
      onConfirm,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      description: "",
      confirmText: "",
      confirmButtonVariant: "primary",
      onConfirm: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };



  // Check if Request Review button should be shown
  const canShowRequestReview = () => {
    // Only show when noticeStatus is ON_WRITE
    if (noticeStatus !== NOTICE_STATUS.ON_WRITE) {
      return false;
    }

    // Show when noticeInfoWriteStatusCd is DONE
    if (noticeInfoWriteStatusCd === NOTICE_STATUS.DONE) {
      return true;
    }

    return false;
  };

  // Define steps array like in verification report
  const steps = [
    {
      label: getWriteStepLabel(),
      icon: WriteIcon,
    },
    {
      label: t("project.notice-infor.review"),
      icon: ReviewIcon,
    },
    {
      label: t("project.notice-infor.notice"),
      icon: NoticeIcon,
    },
  ];

  const getStepStatus = (stepIndex) => {
    if ((noticeStatus === NOTICE_STATUS.REQUEST || noticeStatus === NOTICE_STATUS.ON_WRITE) && stepIndex === 0) return "current";
    if (noticeStatus === NOTICE_STATUS.ON_REVIEW && stepIndex === 1) return "current";
    if (noticeStatus === NOTICE_STATUS.DONE && stepIndex === 2) return "current";
    if (stepIndex < 1 && noticeStatus !== NOTICE_STATUS.REQUEST) return "completed";
    if (stepIndex < 2 && noticeStatus === NOTICE_STATUS.DONE) return "completed";
    return "pending";
  };

  const getConnectorClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "h-1 flex-1 transition-all duration-200";
    if (status === "completed") {
      return `${baseClasses} ${colors.bgStatusSuccess}`;
    }
    return `${baseClasses} ${colors.bgStatusMuted}`;
  };

  // Function to check if step should show CheckedIcon
  const shouldShowCheckedIcon = (stepIndex, step) => {
    const status = getStepStatus(stepIndex);
    // Show CheckedIcon for completed steps OR Notice step when it's current
    return status === "completed" || (status === "current" && step.label === t("project.notice-infor.notice"));
  };

  return (
    <div className="w-full h-40" data-testid={tid("root")}>
      <div className="flex justify-center items-center mt-8" data-testid={tid("steps-track")}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isActive = status === "current";
          const showCheckedIcon = shouldShowCheckedIcon(index, step);
          const Icon = step.icon;

          return (
            <React.Fragment key={index}>
              <div
                className="relative flex flex-col items-center"
                data-testid={tid(`step-${workflowStepSlug(step.label)}`)}
              >
                <div className="relative">
                  {showCheckedIcon ? (
                    <CheckedIcon size={56} />
                  ) : (
                    <Icon size={56} isActive={isActive} />
                  )}
                </div>
                <div
                  className={`absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2 min-w-30 max-[1600px]:max-w-[120px] text-center text-sm font-semibold whitespace-nowrap max-[1600px]:whitespace-normal max-[1600px]:break-words ${isActive && !showCheckedIcon ? colors.textStatusActiveStep : colors.textPrimary}`}
                >
                  {step.label}
                </div>
              </div>

              {index < steps.length - 1 && <div className={`${getConnectorClasses(index)} max-w-40`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Write Step Buttons */}
      {(noticeStatus === NOTICE_STATUS.REQUEST || noticeStatus === NOTICE_STATUS.ON_WRITE) && canManageWorkflow && (
        <div className="flex justify-between items-center mt-16" data-testid={tid("step-write-actions")}>
          <div className="flex space-y-2"></div>
          <div className="flex gap-2">
            {!canShowRequestReview() && (
              <PrimaryButton
                data-testid={tid("write-notice-information-button")}
                size="md"
                onClick={handleOpenNoticeInfoModal}
                disabled={noticeInfoCompleted}
                className="min-w-[200px]"
              >
                {t("project.notice-infor.write-notice-information")}
              </PrimaryButton>
            )}
            {canShowRequestReview() && (
              <PrimaryButton
                data-testid={tid("request-review-button")}
                size="md"
                onClick={() =>
                  showConfirmation(
                    t("project.notice-infor.request-review"),
                    t("project.notice-infor.request-review-notice-confirmation"),
                    t("project.notice-infor.request-review"),
                    "primary",
                    onRequestReview,
                  )
                } 
                className="min-w-[200px]"
              >
                {t("project.notice-infor.request-review")}
              </PrimaryButton>
            )}
          </div>
        </div>
      )}

      {/* ON_REVIEW Status Buttons */}
      {noticeStatus === NOTICE_STATUS.ON_REVIEW && canManageWorkflow && (
        <div className="flex justify-between items-center mt-16" data-testid={tid("step-review-actions")}>
          <div className="flex gap-2">
            <button
              data-testid={tid("reject-review-button")}
              onClick={() =>
                showConfirmation(
                  t("project.notice-infor.confirm-reject-review-title"),
                  t("project.notice-infor.confirm-reject-review-body"),
                  t("common.workflow.reject"),
                  "danger",
                  onRejectReview,
                )
              }
              className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80`}
            >
              {t("project.notice-infor.request-reject-review")}
            </button>
          </div>
          <div className="flex gap-2">
            <PrimaryButton
              data-testid={tid("confirm-notice-button")}
              size="md"
              onClick={() =>
                showConfirmation(
                  t("project.notice-infor.request-confirm-notice"),
                  t("project.notice-infor.confirm-notice-confirmation"),
                  t("project.notice-infor.request-confirm-notice"),
                  "primary",
                  onConfirmNotice,
                )
              } 
              className="min-w-[140px]"
            >
              {t("project.notice-infor.request-confirm-notice")}
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* DONE Status Buttons */}
      {noticeStatus === NOTICE_STATUS.DONE && canManageWorkflow && (
        <div className="flex justify-between items-center mt-16" data-testid={tid("step-notice-actions")}>
          <div className="flex gap-2"></div>
          <div className="flex gap-2">
            <button
              data-testid={tid("re-distribution-button")}
              onClick={() =>
                showConfirmation(
                  t("project.notice-infor.confirm-re-distribution-title"),
                  t("project.notice-infor.confirm-re-distribution-body"),
                  t("project.notice-infor.re-distribution"),
                  "danger",
                  onReDistribution,
                )
              }
              className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80`}
            >
              {t("project.notice-infor.re-distribution")}
            </button>
          </div>
        </div>
      )}

      {/* Notice Information List Modal */}
      <NoticeInformationListModal
        isOpen={showNoticeInfoModal}
        onClose={() => setShowNoticeInfoModal(false)}
        onComplete={() => setNoticeInfoCompleted(true)}
        noticeInfo={noticeInfo}
        noticeInfoComponents={noticeInfoComponents}
        scanComponents={scanComponents}
        updateNoticeInfoNoticeInfoWriteStatus={updateNoticeInfoNoticeInfoWriteStatus}
        isPending={false}
      />


      {/* Confirmation Modal */}
      <ConfirmActionModal
        data-testid={tid("confirmation-modal")}
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText={confirmationModal.confirmText}
        confirmButtonVariant={confirmationModal.confirmButtonVariant}
      />
    </div>
  );
};

export default WorkflowProgressNotice;
